import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { useToolAccess } from '../../tools/ToolAccessContext';
import GuiBuilder from '../GuiBuilder';
import GuiDataPage from '../GuiDataPage';
import {
    useActiveProject,
    useDeploymentsData,
    useDeploymentSelection,
    useModalState
} from './hooks';
import { DeploymentList, CreateDeploymentModal, ShareDeploymentModal, EmbedSettings, AnalyticsView } from './components';
import ReleasePageHeader from './components/ReleasePageHeader';
import NoActiveProject from '../../components/NoActiveProject';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const getDeploymentRouteMode = (pathname) => {
    if (pathname.endsWith('/designer')) return 'designer';
    if (pathname.endsWith('/data')) return 'data';
    if (pathname.endsWith('/integration')) return 'integration';
    if (pathname.endsWith('/analytics')) return 'analytics';
    return 'dashboard';
};

const getDeploymentFamilyId = (deployment, linkedFamilies) => {
    const deploymentFamilyId = deployment?.productFamilyId?.toString();
    if (deploymentFamilyId && deploymentFamilyId.toLowerCase() !== EMPTY_GUID) {
        return deployment.productFamilyId;
    }
    return linkedFamilies?.length > 0 ? linkedFamilies[0].id : null;
};

const resolveDefaultGuiVersionTag = (guiSchemas) => {
    if (!Array.isArray(guiSchemas) || guiSchemas.length === 0) {
        return '1.0.0';
    }
    const published = guiSchemas.find(schema => schema.isPublished);
    if (published?.versionTag) {
        return published.versionTag;
    }
    return guiSchemas[0].versionTag || '1.0.0';
};

/**
 * DeployPage - Main component for managing deployments
 * Handles deployment creation, editing, and embed configuration
 */
export default function DeployPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const routeMode = getDeploymentRouteMode(location.pathname);

    // Custom hooks for state management
    const { activeProjectId, activeProjectName } = useActiveProject();
    
    const {
        deployments,
        linkedFamilies,
        allFamilies,
        releases,
        guiSchemas,
        isLoading,
        isSubmitting,
        error,
        successMsg,
        setError,
        setSuccessMsg,
        setIsSubmitting,
        fetchDeploymentsList,
        fetchReleasesData,
        fetchGuiSchemas,
        loadPageData
    } = useDeploymentsData(activeProjectId);

    const {
        selectedDeployment,
        setSelectedDeployment,
        detailTab,
        setDetailTab,
        editName,
        setEditName,
        editGuiVersionTag,
        setEditGuiVersionTag,
        allowedOrigins,
        setAllowedOrigins,
        editReleaseId,
        setEditReleaseId,
        expiresAt,
        setExpiresAt,
        isActive,
        setIsActive,
        copiedToken,
        setCopiedToken,
        copiedSnippet,
        setCopiedSnippet,
        copiedWidget,
        setCopiedWidget,
        activeEmbedTab,
        setActiveEmbedTab,
        handleConfigureDeployment
    } = useDeploymentSelection(deployments, linkedFamilies, releases);

    const {
        isCreateModalOpen,
        setIsCreateModalOpen,
        overlayMouseDown,
        newDepName,
        setNewDepName,
        newDepFamilyId,
        setNewDepFamilyId,
        newDepGuiVersionTag,
        setNewDepGuiVersionTag,
        newDepReleaseId,
        setNewDepReleaseId,
        newDepPassword,
        setNewDepPassword,
        isShareModalOpen,
        setIsShareModalOpen,
        sharedDeployment,
        setSharedDeployment,
        handleOpenCreateModal
    } = useModalState(linkedFamilies, releases);

    // Load data when active project changes
    useEffect(() => {
        loadPageData();
    }, [activeProjectId, loadPageData]);

    useEffect(() => {
        if (routeMode === 'integration') {
            setDetailTab('embed');
            if (activeProjectId) {
                fetchReleasesData();
                fetchGuiSchemas();
            }
        } else if (routeMode === 'analytics') {
            setDetailTab('analytics');
        }
    }, [routeMode, setDetailTab, activeProjectId, fetchReleasesData, fetchGuiSchemas]);

    const handleOpenDesigner = (deployment = selectedDeployment) => {
        if (deployment) {
            handleConfigureDeployment(deployment);
        }
        navigate('/deploy/designer');
    };

    const handleOpenIntegration = (deployment = selectedDeployment) => {
        if (deployment) {
            handleConfigureDeployment(deployment);
        }
        navigate('/deploy/integration');
    };

    const handleOpenAnalytics = (deployment = selectedDeployment) => {
        if (deployment) {
            handleConfigureDeployment(deployment);
        }
        navigate('/deploy/analytics');
    };

    // Handle toggle active with proper parameters
    const handleToggleActiveWrapper = async () => {
        if (!selectedDeployment) return;

        setError(null);
        setSuccessMsg(null);

        try {
            const res = await apiFetch(`/api/deployments/${selectedDeployment.id}/toggle-active`, {
                method: 'POST'
            });

            if (res.ok) {
                const updated = await res.json();
                setIsActive(updated.isActive);
                setSelectedDeployment(prev => ({ ...prev, isActive: updated.isActive }));
                setSuccessMsg(updated.isActive ? 'Integration token activated.' : 'Integration token deactivated.');
                // Refresh the deployments list
                await fetchDeploymentsList();
            } else {
                setError('Failed to toggle token active status.');
            }
        } catch (err) {
            console.error(err);
            setError('Could not connect to service.');
        }
    };

    // Handle save settings with proper parameters
    const handleSaveSettingsWrapper = async (e) => {
        e.preventDefault();
        if (!selectedDeployment) return;

        setIsSubmitting(true);
        setError(null);
        setSuccessMsg(null);

        try {
            const res = await apiFetch(`/api/deployments/${selectedDeployment.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: editName.trim(),
                    guiVersionTag: editGuiVersionTag.trim(),
                    releaseId: editReleaseId,
                    allowedOrigins: allowedOrigins,
                    expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
                    isActive: isActive
                })
            });

            if (res.ok) {
                setSuccessMsg('Deployment settings updated successfully.');
                const updated = await res.json();
                setSelectedDeployment(updated);
                // Refresh deployments list
                await fetchDeploymentsList();
            } else {
                const errText = await res.text();
                setError(errText || 'Failed to update settings.');
            }
        } catch (err) {
            console.error(err);
            setError('Could not connect to update service.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLayoutSaved = async ({ versionTag, isPublished }) => {
        await fetchGuiSchemas();
        if (!isPublished || !selectedDeployment || !versionTag) {
            return;
        }

        try {
            const res = await apiFetch(`/api/deployments/${selectedDeployment.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: selectedDeployment.name,
                    guiVersionTag: versionTag,
                    releaseId: selectedDeployment.releaseId,
                    allowedOrigins: selectedDeployment.allowedOrigins || '',
                    expiresAt: selectedDeployment.expiresAt || null,
                    isActive: selectedDeployment.isActive
                })
            });

            if (res.ok) {
                const updated = await res.json();
                setSelectedDeployment(updated);
                setEditGuiVersionTag(versionTag);
                await fetchDeploymentsList();
            }
        } catch (err) {
            console.error('Failed to pin published layout version to deployment', err);
        }
    };

    // Handle delete deployment with proper parameters
    const handleDeleteDeploymentWrapper = async (id, name) => {
        if (!await window.confirm(`Are you sure you want to delete the deployment "${name}"?`)) return;

        setError(null);
        setSuccessMsg(null);

        try {
            const res = await apiFetch(`/api/deployments/${id}`, {
                method: 'DELETE'
            });

            if (res.ok) {
                setSuccessMsg('Deployment deleted.');
                if (selectedDeployment?.id === id) {
                    setSelectedDeployment(null);
                }
                await fetchDeploymentsList();
            } else {
                setError('Failed to delete deployment.');
            }
        } catch (err) {
            console.error(err);
            setError('Could not connect to service.');
        }
    };

    // Handle create deployment with proper parameters
    const handleCreateDeploymentWrapper = async (e) => {
        e.preventDefault();
        if (!newDepName.trim() || !newDepFamilyId) return;

        setIsSubmitting(true);
        setError(null);
        setSuccessMsg(null);

        const selectedFam = linkedFamilies.find(f => f.id.toString() === newDepFamilyId);
        const familyName = selectedFam ? selectedFam.name : '';
        
        // Get current version from localStorage
        const activeVersionId = localStorage.getItem('activeVersionId') || '00000000-0000-0000-0000-000000000000';
        const activeVersionName = localStorage.getItem('activeVersionName') || 'Production';
        
        // Generate a name for the new release (e.g., "Deployment: Sedan Configurator Prod - 2026-07-29")
        const releaseName = `Deployment: ${newDepName.trim()} - ${new Date().toISOString().split('T')[0]}`;

        try {
            // Step 1: Create a new release from the current version
            const releaseRes = await apiFetch('/api/releases/publish', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: releaseName,
                    sourceVersionId: activeVersionId
                })
            });

            if (!releaseRes.ok) {
                const text = await releaseRes.text();
                setError(text || 'Failed to create release version.');
                setIsSubmitting(false);
                return;
            }

            const newRelease = await releaseRes.json();
            
            // Step 2: Create the deployment with the new release
            const depRes = await apiFetch('/api/deployments', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    projectId: activeProjectId,
                    name: newDepName.trim(),
                    productFamilyId: newDepFamilyId,
                    productFamilyName: familyName,
                    releaseId: newRelease.id,
                    guiVersionTag: resolveDefaultGuiVersionTag(guiSchemas),
                    allowedOrigins: '',
                    expiresAt: null,
                    demoPassword: newDepPassword.trim() || null
                })
            });

            if (depRes.ok) {
                const created = await depRes.json();
                setNewDepName('');
                setNewDepPassword('');
                setIsCreateModalOpen(false);
                setSharedDeployment(created);
                setIsShareModalOpen(true);
                setSuccessMsg('Deployment created successfully with new release version.');
                handleConfigureDeployment(created);
                await Promise.all([
                    fetchDeploymentsList(),
                    fetchReleasesData(),
                    fetchGuiSchemas()
                ]);
            } else {
                const text = await depRes.text();
                setError(text || 'Failed to create deployment.');
            }
        } catch (err) {
            console.error(err);
            setError('Could not connect to service to create deployment.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <div className="loader"></div>
            </div>
        );
    }

    // No active project state
    if (!activeProjectId) {
        return (
            <NoActiveProject description="Select an active project in the header, or create one from the project menu, to configure deployments." />
        );
    }

    const workspaceProps = {
        selectedDeployment,
        projectId: activeProjectId,
        releases,
        guiSchemas,
        isActive,
        isSubmitting,
        error,
        successMsg,
        editName,
        setEditName,
        editGuiVersionTag,
        setEditGuiVersionTag,
        allowedOrigins,
        setAllowedOrigins,
        editReleaseId,
        setEditReleaseId,
        expiresAt,
        setExpiresAt,
        onToggleActive: handleToggleActiveWrapper,
        onSaveSettings: handleSaveSettingsWrapper,
        copiedToken,
        setCopiedToken,
        copiedSnippet,
        setCopiedSnippet,
        copiedWidget,
        setCopiedWidget,
        activeEmbedTab,
        setActiveEmbedTab,
        onDeploymentUpdated: (updated) => {
            setSelectedDeployment(updated);
            fetchDeploymentsList();
        }
    };

    const renderDeploymentWorkspace = () => {
        if (!selectedDeployment) {
            return (
                <DeploymentSelectionEmptyState
                    deployments={deployments}
                    onSelectDeployment={handleConfigureDeployment}
                    onCreateNew={handleOpenCreateModal}
                    routeMode={routeMode}
                />
            );
        }

        if (routeMode === 'designer') {
            return (
                <DesignerWorkspace
                    selectedDeployment={selectedDeployment}
                    deployments={deployments}
                    linkedFamilies={linkedFamilies}
                    activeProjectId={activeProjectId}
                    onSelectDeployment={handleConfigureDeployment}
                    onBackToDashboard={() => navigate('/deploy')}
                    onLayoutSaved={handleLayoutSaved}
                />
            );
        }

        if (routeMode === 'integration') {
            return (
                <DeploymentSubpageShell
                    title="Integration & Security"
                    description="Manage deployment settings, allowed origins, embed snippets, and token status."
                    selectedDeployment={selectedDeployment}
                    deployments={deployments}
                    onSelectDeployment={handleConfigureDeployment}
                    onBackToDashboard={() => navigate('/deploy')}
                >
                    <div style={{ overflowY: 'auto', minHeight: 0, paddingBottom: '1rem' }}>
                        <EmbedSettings {...workspaceProps} />
                    </div>
                </DeploymentSubpageShell>
            );
        }

        return (
            <DeploymentSubpageShell
                title="Deployment Analytics"
                description="Monitor deployment sessions, completion rate, and embed health."
                selectedDeployment={selectedDeployment}
                deployments={deployments}
                onSelectDeployment={handleConfigureDeployment}
                onBackToDashboard={() => navigate('/deploy')}
            >
                <div style={{ overflowY: 'auto', minHeight: 0, paddingBottom: '1rem' }}>
                    <AnalyticsView selectedDeployment={selectedDeployment} isActive={isActive} />
                </div>
            </DeploymentSubpageShell>
        );
    };

    // Main render
    return (
        <div style={{ padding: '1rem 1.25rem', width: '100%', maxWidth: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: routeMode === 'designer' ? '0.75rem' : '1rem', height: '100%', overflow: 'hidden' }} className="fade-in">

            {routeMode === 'data' ? (
                <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    <GuiDataPage projectId={activeProjectId} linkedFamilies={linkedFamilies} />
                </div>
            ) : routeMode === 'dashboard' ? (
                <DeploymentList
                    deployments={deployments}
                    activeProjectName={activeProjectName}
                    onOpenDesigner={handleOpenDesigner}
                    onOpenIntegration={handleOpenIntegration}
                    onOpenAnalytics={handleOpenAnalytics}
                    onDeleteDeployment={handleDeleteDeploymentWrapper}
                    onCreateNew={handleOpenCreateModal}
                    error={error}
                    successMsg={successMsg}
                />
            ) : (
                renderDeploymentWorkspace()
            )}

            {/* Create Deployment Modal */}
            <CreateDeploymentModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSubmit={handleCreateDeploymentWrapper}
                newDepName={newDepName}
                setNewDepName={setNewDepName}
                newDepFamilyId={newDepFamilyId}
                setNewDepFamilyId={setNewDepFamilyId}
                newDepPassword={newDepPassword}
                setNewDepPassword={setNewDepPassword}
                linkedFamilies={linkedFamilies}
                isSubmitting={isSubmitting}
                overlayMouseDown={overlayMouseDown}
            />
            <ShareDeploymentModal
                isOpen={isShareModalOpen}
                onClose={() => {
                    setIsShareModalOpen(false);
                    setSharedDeployment(null);
                }}
                deployment={sharedDeployment}
                overlayMouseDown={overlayMouseDown}
            />
        </div>
    );
}

const getWorkspaceTitle = (routeMode) => {
    if (routeMode === 'designer') return 'GUI Designer';
    if (routeMode === 'data') return 'GUI data';
    if (routeMode === 'integration') return 'Integration & Security';
    return 'Deployment Analytics';
};

const DeploymentSelectionEmptyState = ({ deployments, onSelectDeployment, onCreateNew, routeMode }) => {
    const { canEditCurrent } = useToolAccess();
    return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
            width: 'min(720px, 100%)',
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '2rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.25rem'
        }}>
            <div style={{ textAlign: 'center' }}>
                <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '2rem', color: 'var(--accent)', marginBottom: '0.75rem' }}></i>
                <h2 style={{ margin: 0, fontSize: '1.35rem', color: 'var(--text-primary)' }}>Choose a deployment</h2>
                <p style={{ margin: '0.35rem 0 0 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                    Select a deployment to open {getWorkspaceTitle(routeMode).toLowerCase()}.
                </p>
            </div>

            {deployments.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '340px', overflowY: 'auto' }}>
                    {deployments.map(deployment => (
                        <button
                            key={deployment.id}
                            type="button"
                            className="btn-secondary"
                            onClick={() => onSelectDeployment(deployment)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '1rem',
                                padding: '0.8rem 1rem',
                                textAlign: 'left'
                            }}
                        >
                            <span style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                                <strong>{deployment.name}</strong>
                                <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{deployment.productFamilyName || deployment.productFamilyId}</span>
                            </span>
                            <i className="fa-solid fa-arrow-right"></i>
                        </button>
                    ))}
                </div>
            ) : (
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                    No deployments exist for the active project yet.
                </div>
            )}

            <button
                type="button"
                className="btn-primary"
                onClick={onCreateNew}
                disabled={!canEditCurrent}
                style={{ alignSelf: 'center', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.65rem 1rem' }}
            >
                <i className="fa-solid fa-plus"></i>
                New Deployment
            </button>
        </div>
    </div>
    );
};

const DeploymentWorkspaceHeader = ({
    title,
    description,
    selectedDeployment,
    deployments,
    onSelectDeployment,
    onBackToDashboard
}) => {
    const handleSelectionChange = (event) => {
        const deployment = deployments.find(d => String(d.id) === event.target.value);
        if (deployment) {
            onSelectDeployment(deployment);
        }
    };

    return (
        <ReleasePageHeader title={title} description={description}>
            <select
                className="input-field"
                value={selectedDeployment?.id || ''}
                onChange={handleSelectionChange}
                style={{ width: '260px', margin: 0, padding: '0.45rem 0.65rem', fontSize: '0.82rem' }}
            >
                {deployments.map(deployment => (
                    <option key={deployment.id} value={deployment.id}>{deployment.name}</option>
                ))}
            </select>
            <span style={{
                padding: '0.25rem 0.55rem',
                borderRadius: '999px',
                fontSize: '0.72rem',
                fontWeight: 700,
                background: selectedDeployment?.isActive ? 'rgba(5,150,105,0.1)' : 'rgba(220,38,38,0.1)',
                color: selectedDeployment?.isActive ? 'var(--success)' : 'var(--error)',
                whiteSpace: 'nowrap'
            }}>
                {selectedDeployment?.isActive ? 'Active' : 'Disabled'}
            </span>
            <button
                type="button"
                className="btn-secondary"
                onClick={onBackToDashboard}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.45rem 0.75rem', fontSize: '0.82rem' }}
            >
                <i className="fa-solid fa-table-columns"></i>
                Dashboard
            </button>
        </ReleasePageHeader>
    );
};

const DeploymentSubpageShell = ({
    title,
    description,
    selectedDeployment,
    deployments,
    onSelectDeployment,
    onBackToDashboard,
    children
}) => (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: '1rem', overflow: 'hidden' }}>
        <DeploymentWorkspaceHeader
            title={title}
            description={description}
            selectedDeployment={selectedDeployment}
            deployments={deployments}
            onSelectDeployment={onSelectDeployment}
            onBackToDashboard={onBackToDashboard}
        />
        {children}
    </div>
);

const DesignerWorkspace = ({
    selectedDeployment,
    deployments,
    linkedFamilies,
    activeProjectId,
    onSelectDeployment,
    onBackToDashboard,
    onLayoutSaved
}) => (
    <DeploymentSubpageShell
        title="GUI Designer"
        description={`${selectedDeployment.name} / ${selectedDeployment.productFamilyName || 'Family'} / ${selectedDeployment.guiVersionTag}`}
        selectedDeployment={selectedDeployment}
        deployments={deployments}
        onSelectDeployment={onSelectDeployment}
        onBackToDashboard={onBackToDashboard}
    >
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <GuiBuilder
                projectId={activeProjectId}
                linkedFamilies={linkedFamilies}
                versionTag={selectedDeployment.guiVersionTag}
                familyId={getDeploymentFamilyId(selectedDeployment, linkedFamilies)}
                familyName={selectedDeployment.productFamilyName}
                onLayoutSaved={onLayoutSaved}
            />
        </div>
    </DeploymentSubpageShell>
);
